import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Form, Badge, Spinner, Alert, InputGroup, Table, Button, Nav
} from 'react-bootstrap';
import { FaFileAlt, FaCrown, FaUsers, FaStar } from "react-icons/fa";
import { getAdminToken } from '../services/authService';
import { useTheme } from "../ThemeContext";
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';

function TemplateManagement() {
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");
  const token = getAdminToken() || localStorage.getItem('token');
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('community'); // 'community' | 'pro'
  const [aiSettings, setAiSettings] = useState(null);
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [aiModel, setAiModel] = useState('gpt-4.1-mini');
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);

  // StaffLayout's page already pads and scrolls this screen; a full-height, padded
  // container inside it only added a second margin and an extra screen of scroll.
  const themeStyles = {
    backgroundColor: "var(--v2-bg)",
    color: "var(--v2-text)",
    padding: 0
  };

  const cardThemeStyles = {
    backgroundColor: "var(--v2-surface)",
    color: "var(--v2-text)",
    border: 'none',
    borderRadius: '12px',
    marginBottom: isMobile ? "1rem" : "1.5rem"
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s3/api/templates/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const fetchAiSettings = async () => {
    try {
      const response = await fetch('/s3/api/admin/ai-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch AI settings');
      }
      const data = await response.json();
      setAiSettings(data);
      setAiModel(data.model || 'gpt-4.1-mini');
      setAiEnabled(Boolean(data.is_enabled));
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchAiSettings();
  }, [token]);

  const handleSaveAiSettings = async () => {
    if (!aiKeyInput.trim() && !aiSettings?.configured) {
      setError('Enter an OpenAI API key before saving.');
      return;
    }
    setAiSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s3/api/admin/ai-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          api_key: aiKeyInput.trim(),
          model: aiModel.trim() || 'gpt-4.1-mini',
          is_enabled: aiEnabled
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to save AI settings');
      }
      setAiSettings(data);
      setAiKeyInput('');
      setMessage('AI settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestAiSettings = async () => {
    setAiTesting(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s3/api/admin/ai-settings/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.details || data.error || 'OpenAI connection test failed');
      }
      setMessage('OpenAI connection test succeeded.');
    } catch (err) {
      setError(err.message);
    } finally {
      setAiTesting(false);
    }
  };

  const handleDeleteAiSettings = async () => {
    if (!window.confirm('Remove the system OpenAI API key?')) return;
    setAiSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s3/api/admin/ai-settings', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to delete AI settings');
      }
      setAiSettings({ configured: false, model: 'gpt-4.1-mini', is_enabled: false });
      setAiModel('gpt-4.1-mini');
      setAiEnabled(false);
      setAiKeyInput('');
      setMessage('AI settings removed.');
    } catch (err) {
      setError(err.message);
    } finally {
      setAiSaving(false);
    }
  };

  // Which scanner JSON major a template renders. Templates are only offered for scans of
  // the same major (1 = pre-2.0 scans, 2 = 2.x scans).
  const handleSetScanVersion = async (templateId, version) => {
    try {
      const response = await fetch(`/s3/api/templates/admin/${templateId}/scan-version`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ version })
      });
      if (!response.ok) throw new Error('Failed to update template scanner version');

      const data = await response.json();
      setMessage(data.message || 'Updated successfully');
      setTemplates(prev => prev.map(t => (t.id === templateId ? { ...t, scan_schema_version: version } : t)));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleTierDefault = async (templateId, tier, currentValue) => {
    try {
      const newValue = !currentValue;
      const response = await fetch(`/s3/api/templates/admin/${templateId}/tier-default`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tier, value: newValue })
      });
      if (!response.ok) throw new Error(`Failed to update ${tier} default status`);

      const data = await response.json();
      setMessage(data.message || 'Updated successfully');
      
      // Update local state
      setTemplates(prev => prev.map(t => {
        if (t.id !== templateId) return t;
        const updated = { ...t };
        if (tier === 'community') {
          updated.is_community_default = newValue;
        } else {
          updated.is_pro_default = newValue;
        }
        updated.is_default = updated.is_community_default || updated.is_pro_default;
        return updated;
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const communityCount = templates.filter(t => t.is_community_default).length;
  const proCount = templates.filter(t => t.is_pro_default).length;

  const tierField = activeTab === 'community' ? 'is_community_default' : 'is_pro_default';
  const tierLabel = activeTab === 'community' ? 'Community' : 'Professional';

  return (
    <Container fluid={isMobile ? true : "lg"} style={themeStyles}>
      <Row className="justify-content-center">
        <Col xs={12}>
          <Card className="shadow-sm" style={cardThemeStyles}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 d-flex align-items-center">
                <FaFileAlt className="me-2" />
                System Templates
              </h5>
              <Button variant="outline-primary" size="sm" onClick={fetchTemplates} disabled={isLoading}>
                {isLoading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
              </Button>
            </Card.Header>
            <Card.Body className={isMobile ? "p-3" : "p-4"}>
              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}

              {/* Tier Tabs */}
              <Nav
                variant="pills"
                activeKey={activeTab}
                onSelect={(key) => setActiveTab(key)}
                className="mb-4"
                style={{ gap: '0.5rem' }}
              >
                <Nav.Item>
                  <Nav.Link
                    eventKey="community"
                    style={{
                      backgroundColor: activeTab === 'community'
                        ? (isDarkMode ? '#0d6efd' : '#0d6efd')
                        : (isDarkMode ? '#2a2a2a' : '#e9ecef'),
                      color: activeTab === 'community' ? '#fff' : (isDarkMode ? '#ccc' : '#495057'),
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: activeTab === 'community' ? 600 : 400,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FaUsers className="me-2" />
                    Community Defaults
                    <Badge
                      bg={activeTab === 'community' ? 'light' : 'secondary'}
                      text={activeTab === 'community' ? 'dark' : 'light'}
                      pill
                      className="ms-2"
                    >
                      {communityCount}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="pro"
                    style={{
                      backgroundColor: activeTab === 'pro'
                        ? (isDarkMode ? '#198754' : '#198754')
                        : (isDarkMode ? '#2a2a2a' : '#e9ecef'),
                      color: activeTab === 'pro' ? '#fff' : (isDarkMode ? '#ccc' : '#495057'),
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: activeTab === 'pro' ? 600 : 400,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FaStar className="me-2" />
                    Professional Defaults
                    <Badge
                      bg={activeTab === 'pro' ? 'light' : 'secondary'}
                      text={activeTab === 'pro' ? 'dark' : 'light'}
                      pill
                      className="ms-2"
                    >
                      {proCount}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Tier description */}
              <Alert variant={activeTab === 'community' ? 'info' : 'success'} className="py-2 mb-3" style={{ fontSize: '0.875rem' }}>
                {activeTab === 'community'
                  ? '🆓 Community defaults are shown to free-tier tenants who cannot create or modify templates.'
                  : '⭐ Professional defaults are shown to Pro tenants who can also create their own custom templates.'}
              </Alert>

              {/* Search */}
              <InputGroup className="mb-4">
                <InputGroup.Text><FaFileAlt color={"var(--v2-text)"} /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={isDarkMode ? 'bg-dark text-light placeholder-light' : ''}
                />
              </InputGroup>

              {isLoading && templates.length === 0 ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No templates found.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover variant={isDarkMode ? 'dark' : ''}>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Created By</th>
                        <th>Tenant</th>
                        <th>Created Date</th>
                        <th title="Scanner JSON version this template renders">Scanner</th>
                        <th>Default</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTemplates.map(template => (
                        <tr key={template.id}>
                          <td>{template.id}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {template.name}
                            </div>
                          </td>
                          <td>{template.creator_name || 'System'}</td>
                          <td>{template.tenant_name || 'Global'}</td>
                          <td>{new Date(template.created_at).toLocaleDateString()}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              aria-label={`Scanner version for ${template.name}`}
                              value={template.scan_schema_version || 1}
                              onChange={(e) => handleSetScanVersion(template.id, Number(e.target.value))}
                            >
                              <option value={1}>v1.x</option>
                              <option value={2}>v2.x</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Check 
                              type="switch"
                              id={`${activeTab}-switch-${template.id}`}
                              checked={Boolean(template[tierField])}
                              onChange={() => handleToggleTierDefault(template.id, activeTab, template[tierField])}
                              label={template[tierField] ? "Default" : ""}
                              className={template[tierField] ? "text-success fw-bold" : ""}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm" style={cardThemeStyles}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 d-flex align-items-center">
                <FaCrown className="me-2" />
                AI Report Builder
              </h5>
              <Badge bg={aiSettings?.configured && aiSettings?.is_enabled ? 'success' : 'secondary'}>
                {aiSettings?.configured && aiSettings?.is_enabled ? 'Enabled' : 'Not Enabled'}
              </Badge>
            </Card.Header>
            <Card.Body className={isMobile ? "p-3" : "p-4"}>
              <Row className="g-3 align-items-end">
                {/* One row only once the page is wide enough: below xl the staff rail leaves
                    too little room, and the Save/Test pair ran out of its 2-column cell. */}
                <Col xs={12} xl={5}>
                  <Form.Label>OpenAI API Key</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder={aiSettings?.masked_api_key || 'sk-...'}
                    value={aiKeyInput}
                    onChange={(e) => setAiKeyInput(e.target.value)}
                    className={isDarkMode ? 'bg-dark text-light' : ''}
                  />
                  {aiSettings?.configured && (
                    <Form.Text>Configured key: {aiSettings.masked_api_key}</Form.Text>
                  )}
                </Col>
                <Col sm={8} xl={3}>
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    value={aiModel}
                    onChange={(e) => setAiModel(e.target.value)}
                    className={isDarkMode ? 'bg-dark text-light' : ''}
                  />
                </Col>
                <Col sm={4} xl={2}>
                  <Form.Check
                    type="switch"
                    id="ai-report-builder-enabled"
                    label="Enabled"
                    checked={aiEnabled}
                    onChange={(e) => setAiEnabled(e.target.checked)}
                  />
                </Col>
                <Col xs={12} xl={2} className="d-flex gap-2">
                  <Button variant="primary" onClick={handleSaveAiSettings} disabled={aiSaving}>
                    {aiSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button variant="outline-secondary" onClick={handleTestAiSettings} disabled={aiTesting || !aiSettings?.configured}>
                    {aiTesting ? 'Testing...' : 'Test'}
                  </Button>
                </Col>
              </Row>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  The key is encrypted server-side and never stored in report templates. Tenant-owned keys can be added later using the same scope model.
                </small>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate('/report-builder?mode=template', { state: { mode: 'template' } })}
                  >
                    AI Build Template
                  </Button>
                  {aiSettings?.configured && (
                    <Button variant="outline-danger" size="sm" onClick={handleDeleteAiSettings} disabled={aiSaving}>
                      Remove Key
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default TemplateManagement;
